import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Empty, Skeleton, Spin, Tooltip, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-admin';
import { useInfinitePages } from '../Common/DataList';
import { formatDateAndTime, formatDateAndTimeFromNow } from '@src/helpers/utils';
import { getTagForChangeTypes, getTagForStates } from '../Common/Tags';
import LoadingVeil from '@src/ui/LoadingVeil';

const { Title } = Typography;

/** the batch the endless scroll fetches */
const PAGE_SIZE = 25;

/**
 * The team's change instances, newest first, scrolling endlessly.
 *
 * Not grouped: on a busy team every change lands on the same day, and a day
 * heading over two hundred rows tells you nothing while costing a line every
 * time it appears.
 *
 * Each entry carries what identifies a change — its state, the application, the
 * team on the other side of it, and the service — because a name and a
 * timestamp alone do not say which of forty similar changes this is.
 */
const ChangeInstanceLogWidget = ({ pov, filterValues, allTeams = false }: { pov: 'CONSUMER' | 'SERVICE_OWNER', filterValues: any, allTeams?: boolean }) => {
    const itemsResource = pov === 'CONSUMER' ? 'service_items' : 'owner_service_items';
    const resource = pov === 'CONSUMER' ? 'change_instances' : 'owner_change_instances';
    const [page, setPage] = useState(1);

    const filter = {
        service_id: filterValues?.service_id,
        consumer_team_id: filterValues?.consumer_team_id,
        application_id: filterValues?.application_id,
        ...(allTeams ? { all_teams: true } : {}),
        light: true,
    };

    const { loading, error, data, total } = useQuery({
        type: 'getList',
        resource,
        payload: {
            pagination: { page, perPage: PAGE_SIZE },
            // what changed most recently, not what changed first
            sort: { field: 'modified', order: 'DESC' },
            filter,
        },
    });

    /* Anything that makes this a different list starts it over. Built from the
       values rather than the object, which is new on every render. */
    const queryKey = useMemo(
        () => JSON.stringify([resource, allTeams, filter.service_id, filter.consumer_team_id, filter.application_id]),
        [resource, allTeams, filter.service_id, filter.consumer_team_id, filter.application_id],
    );

    const { items, hasMore, sentinelRef, settling } = useInfinitePages({
        queryKey,
        records: data || [],
        loading,
        total,
        page,
        setPage,
        // the rail is short, so start the next page well before its end
        prefetchMargin: '200px',
    });

    /* `settling` means nothing has been gathered for this query yet — an empty
       list there is "not yet", not "none". The list the filter is replacing
       stays up under the veil until the new one lands, rather than the rail
       emptying out and filling again. */
    const lastSettled = useRef<any[]>([]);
    if (!settling) lastSettled.current = items;
    const shown = settling ? lastSettled.current : items;

    /* The scroll box outlives the list now, so a new list would land at the
       offset the old one was scrolled to. It starts at the top instead. */
    const bodyRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!settling && bodyRef.current) bodyRef.current.scrollTop = 0;
    }, [queryKey, settling]);

    const body = () => {
        if (error) {
            return <p className="dash-history__note dash-history__note--bad">Change history could not be loaded.</p>;
        }
        if (settling && shown.length === 0) {
            // the first load: a still outline of entries for the veil to cover
            return <Skeleton title={false} paragraph={{ rows: 12 }} />;
        }
        if (shown.length === 0) {
            return (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No change history yet"
                    className="dash-history__empty"
                />
            );
        }

        return (
            <>
                {shown.map((ci: any) => {
                    const name = ci.service_item?.name || '—';
                    /* The team on the other side of the change: whose it is to
                       a service owner, whose service it is to a consumer. Both
                       are in the light payload the rail asks for — as is
                       `application` — so neither needs a fallback. */
                    const counterparty = pov === 'SERVICE_OWNER'
                        ? ci.consumer_team?.name
                        : ci.service_owner_team?.name;

                    /* Two lines and then the tooltip, rather than one line and
                       then nothing: item names run long, and the rail cannot be
                       made wide enough to hold the longest of them without
                       wasting the width on every short one. */
                    const head = (
                        <Tooltip placement="topLeft" title={name}>
                            <span className="dash-entry__name">{name}</span>
                        </Tooltip>
                    );

                    return (
                        <article className="dash-entry" key={ci.id}>
                            {/* What the change is and when it last moved, on the
                                line that opens the entry — the same tags the
                                change instances list uses, so a state means the
                                same thing on both pages. */}
                            <div className="dash-entry__row">
                                {getTagForStates(ci.state)}
                                {ci.change_type && getTagForChangeTypes(ci.change_type)}
                                {ci.modified && (
                                    <Tooltip title={`${formatDateAndTime(ci.modified)} · ${formatDateAndTimeFromNow(ci.modified)}`}>
                                        <time className="dash-entry__when">
                                            {new Date(ci.modified).toLocaleString(undefined, {
                                                day: '2-digit',
                                                month: 'short',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                            })}
                                        </time>
                                    </Tooltip>
                                )}
                            </div>

                            {/* Across every team the item behind an entry is
                                usually another team's, and the page it would
                                open is scoped to the reader's own — a link that
                                answers 404. It stays text until those pages are
                                widened too. */}
                            {ci.service_item?.id && !allTeams ? (
                                <Link className="dash-entry__link" to={`/${itemsResource}/${ci.service_item.id}`}>
                                    {head}
                                </Link>
                            ) : (
                                <span className="dash-entry__link dash-entry__link--dead">{head}</span>
                            )}

                            <p className="dash-entry__who">
                                <span className="dash-entry__app" title={ci.application?.name}>
                                    {ci.application?.name || '—'}
                                </span>
                                {counterparty && (
                                    <>
                                        <span className="dash-entry__sep" aria-hidden="true">·</span>
                                        <span
                                            className="dash-entry__team"
                                            title={`${pov === 'SERVICE_OWNER' ? 'Consumer' : 'Service owner'}: ${counterparty}`}
                                        >
                                            {counterparty}
                                        </span>
                                    </>
                                )}
                                {/* One team of a change is normally the reader's
                                    own and goes without saying. Across every
                                    team it does not, so both sides are named. */}
                                {allTeams && ci.service_owner_team?.name && (
                                    <>
                                        <span className="dash-entry__sep" aria-hidden="true">·</span>
                                        <span
                                            className="dash-entry__team"
                                            title={`Service owner: ${ci.service_owner_team.name}`}
                                        >
                                            {ci.service_owner_team.name}
                                        </span>
                                    </>
                                )}
                            </p>

                            {/* the service name runs to seventy characters, so it
                                gets the line to itself and the ellipsis */}
                            <Tooltip placement="topLeft" title={ci.service?.name}>
                                <p className="dash-entry__service">{ci.service?.name || '—'}</p>
                            </Tooltip>
                        </article>
                    );
                })}

                {/* comes into view, next page loads — nothing to click */}
                {hasMore && !settling && (
                    <div className="dash-history__more" ref={sentinelRef}>
                        <Spin size="small" />
                    </div>
                )}
            </>
        );
    };

    return (
        <section className="dash-history">
            <header className="dash-history__head">
                <Title level={5} className="dash-history__title">Change history</Title>
                {(total ?? 0) > 0 && <span className="dash-history__count">{total}</span>}
            </header>
            {/* Only the list starting over veils it. The next page loading
                has its own spinner at the foot, and veiling the rows the reader
                is scrolling through for it would hide them. The veil sits
                outside the scroll box, so its spinner is in the middle of what
                can be seen rather than of a list that runs far below it. */}
            <LoadingVeil fill loading={settling && !error} label="Loading change history">
                <div className="dash-history__body" ref={bodyRef}>{body()}</div>
            </LoadingVeil>
        </section>
    );
};

export default ChangeInstanceLogWidget;
